import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
} from "@mui/material";
import SwitchToggle from "../../components/SwitchToggle";
import UserSubscriptionWithLogs from "../../features/manageCustomers/userSubscriptionWithLogs";
import LinkedAccountsTable from "../../features/manageCustomers/linkedAccountsTable";
import TaskViewList from "../../features/manageCustomers/taskViewList";
import BudgetStatsView from "../../features/manageCustomers/budgetStatsView";
import { showError, showToast } from "../../constants/toasts";
import {
  useLazyGetUserByIdQuery,
  useLazyGetUserExpenseByIdQuery,
  useLazyGetUserTasksByIdQuery,
  useUpdateUsersMutation,
} from "../../services/user";
import { capitalizeFirst } from "../../utils/commonFunctions";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
// Enum-like options for user status
const USER_STATUS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "suspended", label: "Suspended" },
] as const;

type UserStatus = (typeof USER_STATUS)[number]["value"];
function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
const ManageCustomerDetail = () => {
  const [valueTabs, setValueTabs] = React.useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const navigate = useNavigate();
  const { id } = useParams();
  const [userById] = useLazyGetUserByIdQuery();
  const [getUserTaskById] = useLazyGetUserTasksByIdQuery();
  const [getUserExpenseById] = useLazyGetUserExpenseByIdQuery();

  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [tasks, setTasks] = useState<any>(null);
  const [expenses, setExpenses] = useState<any>(null);
  const [permissions, setPermissions] = useState<any>(null);
  const [userUpdate] = useUpdateUsersMutation();

  // Remove the old Status state and handler since we're using the new dynamic approach

  const getUserStatusCode = (user: any): UserStatus => {
    if (user?.user_status) return user.user_status as UserStatus;
    switch (user?.profile_status) {
      case 1:
        return "active";
      case 2:
        return "suspended";
      case 0:
        return "inactive";
      default:
        return "active";
    }
  };
  const getDataByIdList = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const numericId = Number(id);
      if (Number.isNaN(numericId)) return;
      const response = await userById({ id: numericId }).unwrap();

      if (
        (response as any).status === 200 ||
        (response as any).statusCode === 200
      ) {
        setUser((response as any).data);
        setPermissions({
          calender: (response as any).data?.is_calender,
          goal: (response as any).data?.is_goal,
          budget: (response as any).data?.is_budget,
        });
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };
  const getTasksById = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const numericId = Number(id);
      if (Number.isNaN(numericId)) return;
      const response = await getUserTaskById({ id: numericId }).unwrap();

      if (
        (response as any).status === 200 ||
        (response as any).statusCode === 200
      ) {
        setTasks((response as any).data);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };
  const getExpensesById = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const numericId = Number(id);
      if (Number.isNaN(numericId)) return;
      const response = await getUserExpenseById({ id: numericId }).unwrap();
      if (
        (response as any).status === 200 ||
        (response as any).statusCode === 200
      ) {
        setExpenses((response as any).data);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };

  const handleFingerprintUpdate = async (id: number, status: boolean) => {
    const body = {
      is_fingerprint_applied: status,
    };

    try {
      const response = await userUpdate({ body, id }).unwrap();
      if (response?.status === 200) {
        showToast(`User ${!status ? "deactivated" : "activated"} successfully`);
        getDataByIdList();
      } else {
        // showError("Please try again");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };
  const handlePermissionsUpdate = async (id: number) => {
    const body = {
      is_calender: user?.is_calender,
      is_goal: user?.is_goal,
      is_budget: user?.is_budget,
    };

    try {
      const response = await userUpdate({ body, id }).unwrap();
      if (response?.status === 200) {
        showToast(`Permissions updated successfully`);
        getDataByIdList();
      } else {
        // showError("Please try again");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };
  const handleStatusUpdate = async (id: number, Userstatus: UserStatus) => {
    const body = {
      user_status: Userstatus,
    };

    try {
      const response = await userUpdate({ body, id }).unwrap();
      if (response?.status === 200) {
        const actionText =
          Userstatus === "active"
            ? "activated"
            : Userstatus === "inactive"
            ? "deactivated"
            : "suspended";
        showToast(`User ${actionText} successfully`);
        getDataByIdList();
      } else {
        // showError("Please try again");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };
  useEffect(() => {
    if (id) {
      getDataByIdList();
      getTasksById();
      getExpensesById();
    }
  }, [id]);

  console.log(user, "user data");

  return (
    <>
      <div className="main_title">
        <h1>User Detail</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate("/manage-Users")}>User Management</span>{" "}
          - Customer Detail
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="General Details" {...a11yProps(0)} />
              <Tab label="Subscription" {...a11yProps(1)} />
              <Tab label="Linked Accounts" {...a11yProps(2)} />
              <Tab label="Permissions" {...a11yProps(3)} />
              <Tab label="Tasks" {...a11yProps(4)} />
              <Tab label="Budget Stats" {...a11yProps(5)} />
            </Tabs>
          </Box>
        </div>
      </div>

      {/* General Details */}
      <CustomTabPanel value={valueTabs} index={0}>
        <div className="cards view_page">
          <div className="section_title">
            <h2>Personal Info</h2>
          </div>
          {loading ? (
            <div className="loading">Loading...</div>
          ) : (
            <div className="gap_p">
              <div className="vew_img w_100">
                <h6>Image</h6>
                <figure>
                  <img
                    alt="user"
                    src={
                      user?.profile_picture?.media_file_url ||
                      "/static/images/user_placeholder.png"
                    }
                  />
                </figure>
              </div>
              <div className="w_33">
                <h6>Name</h6>
                <p>{user ? `${user.first_name} ${user.last_name}` : "N/A"}</p>
              </div>
              <div className="w_33">
                <h6>Email</h6>
                <p>{user?.email || "N/A"}</p>
              </div>
              <div className="w_33">
                <h6>Phone</h6>
                <p>
                  {user ? `${user.country_code} ${user.phone_number}` : "N/A"}
                </p>
              </div>
              <div className="w_33">
                <h6>Gender</h6>
                <p>
                  {user?.gender === "male"
                    ? "Male"
                    : user?.gender === "female"
                    ? "Female"
                    : user?.gender
                    ? "Others"
                    : "N/A"}
                </p>
              </div>
              <div className="w_33">
                <h6>Date of Birth</h6>
                <p>
                  {user?.date_of_birth
                    ? new Date(user.date_of_birth).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
              <div className="w_33">
                <h6>Age Group</h6>
                <p>{user?.age_group || "N/A"}</p>
              </div>
              <div className="w_33">
                <h6>Food Prefrence</h6>
                <p>{capitalizeFirst(user?.food_type) || "N/A"}</p>
              </div>
              <div className="w_33">
                <h6>Voice Type</h6>
                <p>
                  {user?.Bot_voice === 1
                    ? "Calm Female"
                    : user?.Bot_voice === 2
                    ? "Calm Male"
                    : user?.Bot_voice
                    ? "others"
                    : "N/A"}
                </p>
              </div>
              <div className="w_33">
                <h6>Languages</h6>
                <p>
                  {user?.preferred_language || "N/A"}
                  {user?.optional_second_language &&
                    `, ${user.optional_second_language}`}
                </p>
              </div>
              <div className="w_33">
                <h6>Subscription & Plan</h6>
                <p>N/A</p>
              </div>
              <div className="w_33">
                <h6>Date Registered</h6>
                <p>
                  {user?.created_at
                    ? new Date(user.created_at).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
              <div className="w_33">
                <h6>Status</h6>
                <div className="form">
                  <Select
                    fullWidth
                    value={getUserStatusCode(user)}
                    onChange={(e) =>
                      handleStatusUpdate(
                        user?.id as number,
                        e.target.value as UserStatus
                      )
                    }
                  >
                    {USER_STATUS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="w_33">
                <h6>Biometric Setup</h6>
                <p>
                  <SwitchToggle
                    defaultChecked={user?.is_fingerprint_applied || false}
                    onChange={(checked) =>
                      handleFingerprintUpdate(user?.id as number, checked)
                    }
                  />
                </p>
              </div>
            </div>
          )}
        </div>
      </CustomTabPanel>

      {/* Subscription Info */}
      <CustomTabPanel value={valueTabs} index={1}>
        <div className="cards view_page">
          <div className="section_title">
            <h2>Subscription History</h2>
          </div>
          <UserSubscriptionWithLogs />
        </div>
      </CustomTabPanel>

      {/* Linked Accounts */}
      <CustomTabPanel value={valueTabs} index={2}>
        <div className="cards view_page">
          <div className="section_title">
            <h2>Linked Accounts</h2>
          </div>
          <LinkedAccountsTable
            linkedAccounts={user?.linked_account || []}
            onRoleUpdate={getDataByIdList}
          />
        </div>
      </CustomTabPanel>

      {/* Permissions */}
      <CustomTabPanel value={valueTabs} index={3}>
        <div className="cards view_page">
          <div className="section_title">
            <h2>Permissions Granted</h2>
          </div>
          <div className="form">
            <div className="control_group">
              <FormGroup className="list">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={user?.is_goal}
                      onChange={(e) =>
                        setUser((prev: any) => ({
                          ...prev,
                          is_goal: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Goals"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={user?.is_budget}
                      onChange={(e) =>
                        setUser((prev: any) => ({
                          ...prev,
                          is_budget: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Budgets"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={user?.is_calender}
                      onChange={(e) =>
                        setUser((prev: any) => ({
                          ...prev,
                          is_calender: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Calendar"
                />
              </FormGroup>
            </div>
            <div className="form_btn">
              <Button
                className="btn btn_primary"
                onClick={() => handlePermissionsUpdate(user?.id as number)}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </CustomTabPanel>

      {/* Task */}
      <CustomTabPanel value={valueTabs} index={4}>
        <div className="cards view_page">
          <div className="section_title">
            <h2>Tasks</h2>
          </div>
          <TaskViewList tasks={tasks?.response_object || []} />
        </div>
      </CustomTabPanel>

      {/* Budget Stats */}
      <CustomTabPanel value={valueTabs} index={5}>
        <div className="stats_boxes gap_m">
          <div className="cards">
            <figure>
              <img src="/static/images/money_icon.svg" alt="" />
            </figure>
            <h2>AED {expenses?.total_expense_sum}</h2>
            <p>Expense</p>
          </div>
          <div className="cards">
            <figure>
              <img src="/static/images/money_icon.svg" alt="" />
            </figure>
            <h2>AED 1000</h2>
            <p>My Budget</p>
          </div>
        </div>
        <BudgetStatsView expenseData={expenses} />
      </CustomTabPanel>
    </>
  );
};

export default ManageCustomerDetail;
